/**
 * Client upload queue.
 *
 * Per file: hash → ask the API to authorise and presign → PUT the parts
 * straight to object storage → tell the API to finalise. The bytes never touch
 * our server, so a 300-photo import costs the API a few kilobytes of JSON.
 *
 * Design notes worth knowing:
 *  - Hashing happens *before* the transfer, which is what makes "you already
 *    have this photo" cost zero bytes instead of a full re-upload.
 *  - Three files upload concurrently. More saturates a phone's uplink and makes
 *    every individual file slower without finishing the batch any sooner.
 *  - Parts retry with backoff; a failed part does not restart the file.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import {
  bytesToHex,
  hashBlob,
  isAcceptedMime,
  mediaKindForMime,
  mimeForFilename,
} from "@fc/core";
import { trpcClient } from "../../lib/trpc.js";
import { sealFileKey, tagForGroup } from "../../lib/keyring.js";
import {
  UndecodableError,
  canEncryptEndToEnd,
  prepareUpload,
} from "../../lib/mediaPipeline.js";
import { useGroupKey } from "../../lib/useGroupKey.js";
import { openFilePicker } from "../../lib/filePicker.js";

export type UploadStatus =
  | "queued"
  | "hashing"
  /**
   * Resizing and sealing, before a single byte is sent.
   *
   * Its own step because it is the slowest part of an upload on a phone — a
   * few seconds for a large photograph — and a progress bar that sat at zero
   * through it would read as a stall.
   */
  | "sealing"
  | "uploading"
  | "finalising"
  /**
   * Bytes are safely stored and the server is now decoding, orienting and
   * building the thumbnail ladder.
   *
   * This is deliberately not "done". A JPEG whose bytes are garbage transfers
   * perfectly and *then* fails to decode — reporting "Added" at the end of the
   * transfer tells the user their photo is safe when it is not.
   */
  | "processing"
  | "done"
  | "duplicate"
  | "failed"
  | "cancelled";

/** What the server says became of a file, once it has finished with it. */
export type ServerFileStatus = "uploading" | "processing" | "ready" | "failed";

export interface UploadTask {
  id: string;
  file: File;
  status: UploadStatus;
  /** 0–1 across hashing and transfer combined. */
  progress: number;
  uploadedBytes: number;
  error?: string;
  fileId?: string;
  /**
   * True when this file could not be sealed in the browser and went up in the
   * clear.
   *
   * Surfaced rather than swallowed. A group where some photographs are
   * end-to-end encrypted and others are not is a fact the person who put them
   * there is entitled to know, and the alternative — quietly storing a
   * readable copy under a promise of the opposite — is the worst option
   * available.
   */
  unsealed?: boolean;
}

const MAX_CONCURRENT_FILES = 3;
/** Sealing is a fixed slice of the bar, for the same reason hashing is. */
const SEAL_SHARE = 0.15;
const MAX_PART_RETRIES = 4;
/** Hashing is a fixed slice of the progress bar so it never appears stuck. */
const HASH_SHARE = 0.1;

/**
 * How many files may be *sealed* at once, as distinct from transferred.
 *
 * Transfers are bounded by the uplink and cost almost no memory. Sealing is the
 * opposite: decoding a twelve-megapixel photograph produces about 48 MB of raw
 * pixels outside the JavaScript heap, and the file's own bytes and its
 * ciphertext sit beside it. Three of those at once is roughly 150 MB of peak
 * usage per batch — comfortably inside a desktop tab and comfortably outside
 * what Safari will let an iPhone tab hold, which is why adding ten photographs
 * from a phone failed where the same ten from a laptop did not.
 *
 * So the two limits are separated. One decode at a time on a phone, two
 * elsewhere, while three files still move over the network.
 */
const MAX_CONCURRENT_SEALS = isMemoryConstrained() ? 1 : 2;

/**
 * Whether this is a device that kills the tab rather than reporting an error.
 *
 * Deliberately about the *engine*, not the brand: every iOS browser is Safari
 * underneath, so the per-tab memory ceiling applies to all of them, and an
 * iPad reports itself as a Mac. Wrong in one direction costs a little upload
 * throughput; wrong in the other costs the upload.
 */
function isMemoryConstrained(): boolean {
  if (typeof navigator === "undefined") return false;
  const iOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  // `deviceMemory` is Chromium-only and absent on Safari, so it can only ever
  // add devices to this set, never remove the ones above.
  const small = (navigator as { deviceMemory?: number }).deviceMemory;
  return iOS || (small !== undefined && small <= 4);
}

/**
 * A counting semaphore, small enough not to be worth a dependency.
 *
 * Used for one thing: keeping the memory-hungry part of an upload serialised
 * while the network-bound part is not.
 */
function createGate(limit: number) {
  let active = 0;
  const waiting: (() => void)[] = [];

  return async function withGate<T>(work: () => Promise<T>): Promise<T> {
    if (active >= limit)
      await new Promise<void>((resolve) => waiting.push(resolve));
    active += 1;
    try {
      return await work();
    } finally {
      active -= 1;
      waiting.shift()?.();
    }
  };
}

const withSealGate = createGate(MAX_CONCURRENT_SEALS);

/**
 * Read a picked file's bytes, once, with one retry.
 *
 * On iOS a `File` from the photo library is a handle to something the system
 * owns, and it can stop being readable while a long batch is still working
 * through the queue — the picker's temporary copy is reclaimed, or the photo is
 * still coming down from iCloud. The browser reports that as a `NotReadableError`
 * with no message worth showing, which is how "uploading more than ten photos
 * from an iPhone fails" arrived as an error nobody could describe.
 *
 * One retry after a pause recovers the transient form of it. The rest is
 * reported in words that name the actual problem.
 */
async function readFileOnce(file: File): Promise<Uint8Array> {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      return new Uint8Array(await file.arrayBuffer());
    } catch (error) {
      const name = error instanceof Error ? error.name : "";
      if (
        attempt === 0 &&
        (name === "NotReadableError" || name === "NotFoundError")
      ) {
        await new Promise((resolve) => setTimeout(resolve, 600));
        continue;
      }
      throw new Error(
        name === "NotReadableError" || name === "NotFoundError"
          ? `${file.name} couldn't be read from this device. If it is stored in iCloud, open it in Photos once so it downloads, then try again.`
          : `${file.name} couldn't be read (${name || "unknown error"}).`,
      );
    }
  }
  throw new Error(`${file.name} couldn't be read from this device.`);
}

/**
 * Read one slice of a picked file, with the same one-retry as a whole read.
 *
 * A `Blob` slice is a promise to fetch bytes later, and on iOS the handle
 * behind it can go stale between one part and the next. Reading it here means
 * a failure lands on the part that failed rather than surfacing minutes later
 * as a completion error with nothing to point at.
 */
async function readChunk(
  source: Blob,
  start: number,
  end: number,
  name: string,
): Promise<Uint8Array> {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      return new Uint8Array(await source.slice(start, end).arrayBuffer());
    } catch (error) {
      const errorName = error instanceof Error ? error.name : "";
      if (
        attempt === 0 &&
        (errorName === "NotReadableError" || errorName === "NotFoundError")
      ) {
        await new Promise((resolve) => setTimeout(resolve, 600));
        continue;
      }
      throw new Error(
        `${name} could not be read from this device. If it is stored in iCloud, open it in Photos once so it downloads, then try again.`,
      );
    }
  }
  throw new Error(`${name} could not be read from this device.`);
}
export interface UseUploadQueueOptions {
  groupId: string;
  folderId: string | null;
  onComplete?: () => void;
}

export function useUploadQueue({
  groupId,
  folderId,
  onComplete,
}: UseUploadQueueOptions) {
  /*
   * Whether this group can be written to at all.
   *
   * A locked keyring must not fall through to an unencrypted upload. That
   * would be the worst outcome available: the photograph reaches storage in
   * the clear, nothing warns anybody, and the group looks protected because
   * every other file in it is. Refusing is the safe failure.
   */
  const keyState = useGroupKey(groupId);

  const [tasks, setTasks] = useState<UploadTask[]>([]);
  const running = useRef(0);
  const queue = useRef<UploadTask[]>([]);
  const cancelled = useRef(new Set<string>());

  const update = useCallback((id: string, patch: Partial<UploadTask>) => {
    setTasks((current) =>
      current.map((task) => (task.id === id ? { ...task, ...patch } : task)),
    );
  }, []);

  const runOne = useCallback(
    async (task: UploadTask) => {
      const { file } = task;
      // Kept outside the try so the catch can release the server-side
      // reservation. Without this a failure leaves a row stuck on "uploading",
      // which the gallery renders as "Processing" until the 24-hour sweeper
      // notices — the user sees a photo that never finishes and cannot retry.
      let started: { fileId: string; uploadId: string } | null = null;

      try {
        /*
         * ── Validate before doing any work ──────────────────────────────
         *
         * The browser is the first source and the filename is the second.
         * Picking a clip from an iOS photo library can produce a `File` whose
         * `type` is empty, and the old check turned that into "That file type
         * isn't supported" — for a `.mov` the service accepts, with nothing the
         * person could do about it. The server still checks the magic bytes.
         */
        const mime = isAcceptedMime(file.type)
          ? file.type
          : (mimeForFilename(file.name) ?? "");

        if (!isAcceptedMime(mime)) {
          update(task.id, {
            status: "failed",
            error: `${file.type || "That file type"} isn't supported.`,
          });
          return;
        }

        /*
         * A shut keyring only blocks what would otherwise have been sealed.
         *
         * The first version of this refused every upload, which was too broad:
         * a video is never sealed in the browser — nothing here can transcode
         * it — so a locked keyring changes nothing about where it ends up, and
         * refusing it only stopped people adding videos. What must not happen
         * is a photograph quietly taking the clear path because the keyring
         * happened to be shut.
         */
        /*
         * `awaiting-grant` belongs in this list and was missing from it.
         *
         * A member who has joined an encrypted group but has not yet been
         * handed the key is not `ready`, so nothing sealed — and nothing here
         * refused either, so the photograph went to storage in the clear,
         * into a group where every other file is sealed. Exactly the failure
         * the paragraph above warns about, arriving through a state that did
         * not exist when that paragraph was written.
         */
        if (
          (keyState === "locked" ||
            keyState === "failed" ||
            keyState === "awaiting-grant") &&
          canEncryptEndToEnd(mime)
        ) {
          update(task.id, {
            status: "failed",
            error:
              keyState === "locked"
                ? "Sign in again before adding photos — the key that protects them isn't open."
                : keyState === "awaiting-grant"
                  ? "You are in this group but do not have its key yet. Ask somebody already in it to open the group once, then try again."
                  : "This group's key could not be opened, so photos can't be added to it.",
          });
          return;
        }

        const willSeal = keyState === "ready" && canEncryptEndToEnd(mime);

        /*
         * ── Read, once ──────────────────────────────────────────────────
         *
         * A file that is going to be sealed used to be read three times: the
         * streaming hash walked it, `createImageBitmap` read it again, and
         * sealing read it a third time. Each read is a fresh trip to a handle
         * the operating system owns, and on iOS that handle can stop being
         * valid part-way through a long batch — so a twenty-photo import had
         * sixty chances to lose one, and did.
         *
         * Videos keep the streaming path. They are not sealed here, and a 5 GB
         * clip must never be pulled into memory to be hashed.
         */
        update(task.id, { status: "hashing" });
        const bytes = willSeal ? await readFileOnce(file) : null;
        const sha256 = bytes
          ? bytesToHex(
              new Uint8Array(
                await crypto.subtle.digest("SHA-256", bytes as BufferSource),
              ),
            )
          : await hashBlob(file, {
              onProgress: (ratio) =>
                update(task.id, { progress: ratio * HASH_SHARE }),
            });
        if (cancelled.current.has(task.id)) return;

        /*
         * ── Seal ────────────────────────────────────────────────────────
         *
         * Everything the server will hold is produced here: the resized
         * derivatives and the sealed original. A group that predates
         * encryption has no key and takes the old path; anything the browser
         * cannot decode — video, for now — does too, and the interface says so
         * rather than implying both are equally protected.
         */
        let sealed = null;
        if (bytes) {
          update(task.id, { status: "sealing", progress: HASH_SHARE });
          try {
            /*
             * Gated, so only one photograph is decoded at a time on a phone.
             * The transfers either side of this stay parallel — it is the
             * decode that costs the memory, not the upload.
             */
            sealed = await withSealGate(() =>
              prepareUpload(bytes, mime, (fileKey) =>
                sealFileKey(groupId, fileKey),
              ),
            );
          } catch (error) {
            /*
             * The file is fine; this browser just cannot open it. HEIC on
             * anything but Safari is the case that matters, and refusing the
             * upload over it would mean an Android phone could not receive a
             * photograph an iPhone took.
             *
             * So it goes up in the clear and says so. Anything else is a real
             * failure and is treated as one.
             */
            if (!(error instanceof UndecodableError)) throw error;
            update(task.id, { unsealed: true });
          }
        }
        if (cancelled.current.has(task.id)) return;

        /*
         * What identifies these bytes to the server.
         *
         * For a sealed file the plaintext hash stays in this browser and never
         * goes anywhere. The server is told the hash of the *ciphertext*, which
         * is what it actually stores, and a tag keyed with the group key, which
         * is what lets it recognise the same photograph uploaded twice without
         * being able to recognise any particular photograph at all.
         */
        const identity = sealed
          ? {
              sha256: sealed.sealedSha256,
              contentTag: await tagForGroup(groupId, sha256),
            }
          : { sha256 };

        // ── Authorise and presign ───────────────────────────────────────
        const plan = await trpcClient.uploads.create.mutate({
          groupId,
          /* What the picker chose, when the screen itself had no album. */
          folderId: folderOverride.current ?? folderId,
          name: file.name,
          mime,
          sizeBytes: file.size,
          ...identity,
          takenAt: file.lastModified
            ? new Date(file.lastModified).toISOString()
            : null,
          clientUploadId: task.id,
          ...(sealed
            ? {
                encryption: "e2e" as const,
                wrappedFileKey: sealed.wrappedFileKey,
                encryptedSizeBytes: sealed.original.length,
                variants: sealed.variants.map((variant) => ({
                  variant: variant.variant,
                  format: variant.format,
                  width: variant.width,
                  height: variant.height,
                  encryptedSizeBytes: variant.ciphertext.length,
                })),
              }
            : {}),
        });

        // The group already has these exact bytes: nothing to transfer.
        if (plan.mode === "deduplicated") {
          update(task.id, {
            status: "duplicate",
            progress: 1,
            fileId: plan.fileId,
            uploadedBytes: 0,
          });
          return;
        }

        // A blob that already exists elsewhere: linked, no transfer either.
        if (plan.mode === "linked") {
          update(task.id, { status: "done", progress: 1, fileId: plan.fileId });
          return;
        }

        started = { fileId: plan.fileId, uploadId: plan.uploadId };
        const startShare = sealed ? HASH_SHARE + SEAL_SHARE : HASH_SHARE;
        update(task.id, { status: "uploading", progress: startShare });

        /*
         * ── Transfer ────────────────────────────────────────────────────
         *
         * What goes up is the sealed body, not the file. They differ in length
         * — sealing adds a header and a tag per chunk — so the part boundaries
         * and the progress denominator both have to come from the body that is
         * actually being sent, not from `file.size`.
         */
        /*
         * The sealed bytes are sent as they are, not wrapped in a `Blob`.
         *
         * `new Blob([ciphertext])` copies, so a 12 MB photograph briefly held
         * its plaintext, its ciphertext and a second copy of the ciphertext at
         * once. `fetch` accepts a typed array directly, and `subarray` below
         * gives each part a view rather than another copy.
         */
        const body: Blob | Uint8Array = sealed ? sealed.original : file;
        const bodySize = sealed ? sealed.original.length : file.size;

        const parts: { partNumber: number; etag: string }[] = [];
        let sent = 0;

        const report = () => {
          const ratio = bodySize > 0 ? sent / bodySize : 1;
          update(task.id, {
            progress: startShare + ratio * (1 - startShare),
            uploadedBytes: sent,
          });
        };

        if (plan.mode === "single") {
          const etag = await putWithRetry(
            plan.url,
            body,
            task.id,
            cancelled.current,
            true,
            plan.contentType,
          );
          parts.push({ partNumber: 1, etag });
          sent = bodySize;
          report();
        } else {
          for (const part of plan.parts) {
            if (cancelled.current.has(task.id)) return;

            const start = (part.partNumber - 1) * plan.partSize;
            const end = Math.min(start + plan.partSize, bodySize);
            const wanted = end - start;

            /*
             * Each part is read into memory and checked before it is sent.
             *
             * `Blob.slice` is lazy: the bytes are fetched from the operating
             * system when `fetch` reads the body, part by part, over the
             * minutes a large upload takes. On iOS that handle can stop being
             * valid part-way through — the picker's temporary copy is
             * reclaimed, or the photo is still coming down from iCloud — and
             * the read then yields *fewer bytes than asked for* rather than
             * failing. Storage accepts the short part happily, and the upload
             * dies at the very end, on completion, as "storage didn't respond
             * while finishing that upload". Retrying re-reads the same stale
             * handle and fails identically, which is why retrying never
             * helped.
             *
             * Reading here turns that into an honest error at the part that
             * actually failed, with a sentence naming the cause. One part is
             * eight megabytes, so this costs a bounded amount of memory even
             * for a very large video.
             */
            let chunk: Uint8Array;
            if (body instanceof Uint8Array) {
              // Already in memory; `subarray` is a view and copies nothing.
              chunk = body.subarray(start, end);
            } else {
              chunk = await readChunk(body, start, end, file.name);
            }

            if (chunk.length !== wanted) {
              throw new Error(
                `${file.name} could not be read from this device — it gave ${String(chunk.length)} bytes where ${String(wanted)} were expected. If it is stored in iCloud, open it in Photos once so it downloads, then try again.`,
              );
            }

            const etag = await putWithRetry(
              part.url,
              chunk,
              task.id,
              cancelled.current,
              false,
            );

            parts.push({ partNumber: part.partNumber, etag });
            sent += end - start;
            report();
          }
        }

        /*
         * The derivatives, to the URLs issued alongside the original.
         *
         * Uploaded before `complete` rather than after, because `complete` is
         * what makes the file visible — and a photograph that appears in the
         * grid a moment before its thumbnail exists is a broken tile that
         * fixes itself, which reads as a bug.
         */
        for (const upload of plan.variantUploads ?? []) {
          const made = sealed?.variants.find(
            (candidate) =>
              candidate.variant === upload.variant &&
              candidate.format === upload.format,
          );
          if (!made) continue;

          await putWithRetry(
            upload.url,
            made.ciphertext,
            task.id,
            cancelled.current,
            true,
            upload.contentType,
          );
        }

        if (cancelled.current.has(task.id)) {
          await trpcClient.uploads.abort
            .mutate({ groupId, fileId: plan.fileId, uploadId: plan.uploadId })
            .catch(() => {});
          update(task.id, { status: "cancelled" });
          return;
        }

        // ── Finalise ────────────────────────────────────────────────────
        update(task.id, { status: "finalising", progress: 0.99 });
        const finished = await trpcClient.uploads.complete.mutate({
          groupId,
          fileId: plan.fileId,
          uploadId: plan.uploadId,
          parts,
        });

        /*
         * A sealed file is finished the moment it lands — there is no worker
         * step it is waiting for. Saying "Processing" would be a lie, and one
         * that never resolves, because nothing is ever going to change it.
         */
        update(task.id, {
          status: finished.status === "ready" ? "done" : "processing",
          progress: 1,
          fileId: plan.fileId,
        });
      } catch (error) {
        if (started) {
          await trpcClient.uploads.abort
            .mutate({
              groupId,
              fileId: started.fileId,
              uploadId: started.uploadId,
            })
            .catch(() => {});
        }
        /*
         * Kept in full, and also written to the console.
         *
         * "Upload failed." was all this ever said, which made a real fault
         * unreportable: the person watching it could describe the symptom and
         * nothing else, and neither could anybody they showed it to. A
         * `DOMException` in particular carries its meaning in `name` and leaves
         * `message` empty, so the one useful word was the one being discarded.
         */
        const detail = describeError(error);
        console.error("[upload]", file.name, detail, error);
        update(task.id, { status: "failed", error: detail });
      }
    },
    [groupId, folderId, keyState, update],
  );

  const pump = useCallback(() => {
    while (running.current < MAX_CONCURRENT_FILES && queue.current.length > 0) {
      const next = queue.current.shift();
      if (!next) break;

      running.current += 1;
      void runOne(next).finally(() => {
        running.current -= 1;
        if (queue.current.length > 0) pump();
        else if (running.current === 0) onComplete?.();
      });
    }
  }, [runOne, onComplete]);

  /** Set by `pick` when the caller chose an album for this batch. */
  const folderOverride = useRef<string | null | undefined>(undefined);

  const enqueue = useCallback(
    (files: File[]) => {
      // Photos before videos: the grid fills quickly with things to look at
      // rather than waiting behind one large clip.
      const ordered = [...files].sort((a, b) => {
        const aKind = mediaKindForMime(a.type) === "video" ? 1 : 0;
        const bKind = mediaKindForMime(b.type) === "video" ? 1 : 0;
        return aKind - bKind || a.size - b.size;
      });

      const created = ordered.map<UploadTask>((file, index) => ({
        id: `up_${Date.now().toString(36)}_${index}_${Math.random().toString(36).slice(2, 8)}`,
        file,
        status: "queued",
        progress: 0,
        uploadedBytes: 0,
      }));

      setTasks((current) => [...current, ...created]);
      queue.current.push(...created);
      pump();
    },
    [pump],
  );

  /**
   * Opens the OS picker. Kept here so every entry point shares one accept list.
   *
   * The optional album is for the case where the screen has none of its own:
   * pressing “Add photos” from All albums used to file everything nowhere,
   * because the folder came from the URL and there was nothing in it. The
   * caller now asks first and passes the answer through here.
   */
  const pick = useCallback(
    (intoFolderId?: string | null) => {
      /*
       * A ref rather than state: this is read by the worker that is about to
       * start, and a re-render between choosing and enqueueing would be one
       * more thing that has to happen in the right order.
       */
      if (intoFolderId !== undefined) folderOverride.current = intoFolderId;

      openFilePicker({
        accept:
          "image/jpeg,image/png,image/webp,image/heic,image/avif,video/mp4,video/quicktime",
        multiple: true,
        onPick: enqueue,
      });
    },
    [enqueue],
  );

  const cancel = useCallback(
    (id: string) => {
      cancelled.current.add(id);
      queue.current = queue.current.filter((task) => task.id !== id);
      update(id, { status: "cancelled" });
    },
    [update],
  );

  const retry = useCallback(
    (id: string) => {
      setTasks((current) => {
        const task = current.find((entry) => entry.id === id);
        if (task) {
          cancelled.current.delete(id);
          queue.current.push({
            ...task,
            status: "queued",
            progress: 0,
            error: undefined,
          });
          pump();
        }
        return current.map((entry) =>
          entry.id === id
            ? { ...entry, status: "queued", progress: 0, error: undefined }
            : entry,
        );
      });
    },
    [pump],
  );

  /**
   * Fold the server's view of each file back into the panel.
   *
   * The queue only ever knows whether the *transfer* worked. Whether the photo
   * actually decoded is decided later, by the worker, and reaches the client
   * through the gallery poll — so the caller hands us that verdict here rather
   * than this hook opening a second subscription for it.
   */
  const applyServerStatuses = useCallback(
    (statuses: ReadonlyMap<string, ServerFileStatus>) => {
      if (statuses.size === 0) return;

      setTasks((current) => {
        let changed = false;

        const next = current.map((task) => {
          if (task.fileId === undefined || task.status !== "processing")
            return task;

          const status = statuses.get(task.fileId);
          if (status === undefined) return task;

          if (status === "ready") {
            changed = true;
            return { ...task, status: "done" as const };
          }
          if (status === "failed") {
            changed = true;
            return {
              ...task,
              status: "failed" as const,
              // Distinguished from a transfer failure on purpose: retrying the
              // upload will not help, and saying "Upload failed" would send the
              // user round a loop that cannot succeed.
              error:
                "Uploaded, but we couldn't process this file. It may be corrupt.",
            };
          }
          return task;
        });

        // Returning the same array keeps React from re-rendering on every poll.
        return changed ? next : current;
      });
    },
    [],
  );

  /**
   * Clear everything that has stopped moving, failures included.
   *
   * Keeping failed rows back would be defensible on its own, but the control is
   * labelled "Dismiss" and sits beside "Retry all" — a button that visibly does
   * nothing is worse than losing an error the user has already read.
   */
  const clearFinished = useCallback(() => {
    setTasks((current) =>
      current.filter(
        (task) =>
          !["done", "duplicate", "cancelled", "failed"].includes(task.status),
      ),
    );
  }, []);

  const active = useMemo(
    () => tasks.filter((task) => task.status !== "cancelled"),
    [tasks],
  );

  const totals = useMemo(() => {
    const totalBytes = active.reduce((sum, task) => sum + task.file.size, 0);
    const uploadedBytes = active.reduce(
      (sum, task) => sum + task.uploadedBytes,
      0,
    );
    const completedCount = active.filter((task) =>
      ["done", "duplicate"].includes(task.status),
    ).length;
    const failed = active.filter((task) => task.status === "failed");
    const progress =
      active.length === 0
        ? 0
        : active.reduce((sum, task) => sum + task.progress, 0) / active.length;

    // A batch is finished when nothing is still moving — including when
    // everything failed. Without this the dock sits at 99% forever and the
    // user has no idea anything went wrong. "processing" is still moving: the
    // bytes have landed but the server has not yet said whether they are good.
    const settled = active.every((task) =>
      ["done", "duplicate", "failed"].includes(task.status),
    );

    return {
      totalBytes,
      uploadedBytes,
      completedCount,
      progress,
      failedCount: failed.length,
      firstError: failed[0]?.error,
      settled,
    };
  }, [active]);

  return {
    tasks,
    active,
    enqueue,
    pick,
    cancel,
    retry,
    applyServerStatuses,
    clearFinished,
    ...totals,
  };
}

/**
 * Turn whatever was thrown into something worth putting on screen.
 *
 * The cases that matter are the ones with no message. `TypeError: Failed to
 * fetch` is what a phone losing its connection looks like; a `DOMException`
 * from a canvas or a decoder often has an empty `message` and says everything
 * in its `name`. Both used to arrive as "Upload failed."
 */
function describeError(error: unknown): string {
  /*
   * A storage refusal carries its reason now, and it is worth showing.
   *
   * "Storage didn't respond while finishing that upload" was the same sentence
   * for short parts, an expired upload and an unreachable bucket — three
   * different problems with three different answers, reported identically. The
   * reason is appended rather than replacing the sentence, because the sentence
   * is the part somebody can act on.
   */
  const details = (error as { data?: { details?: { reason?: unknown } } })?.data
    ?.details;
  if (typeof details?.reason === "string" && details.reason.length > 0) {
    const base =
      error instanceof Error ? error.message : "That upload could not be finished.";
    return `${base} (${details.reason})`;
  }

  if (!(error instanceof Error)) return "Upload failed for an unknown reason.";

  if (
    error.name === "QuotaExceededError" ||
    /allocation|out of memory/i.test(error.message)
  ) {
    return "This device ran out of memory while preparing the photo. Try adding fewer at a time.";
  }
  if (error.name === "TypeError" && /fetch|network/i.test(error.message)) {
    return "The connection dropped during the upload. It will work on a retry.";
  }
  if (error.message) return error.message;
  return `Upload failed (${error.name || "unknown error"}).`;
}

/**
 * PUT one part, retrying transient failures with exponential backoff.
 *
 * A 4xx is a decision — a bad signature, an expired URL — and retrying it just
 * wastes the user's data allowance. Only network errors and 5xx get another go.
 */
async function putWithRetry(
  url: string,
  body: Blob | Uint8Array,
  taskId: string,
  cancelledIds: Set<string>,
  isSinglePart: boolean,
  /**
   * The content type the URL was signed with, where there was one.
   *
   * A presigned PUT signs `content-type` into the signature, so the request
   * has to carry that exact header or storage answers 403
   * SignatureDoesNotMatch. `fetch` sends no content type at all for a
   * `Uint8Array` body and the blob's own type for a `Blob` — neither of
   * which is what was signed. Part uploads are different and pass nothing:
   * `UploadPart` has no content type in its signature, and adding an
   * unsigned header there would be harmless but pointless.
   */
  contentType?: string,
): Promise<string> {
  let lastError: unknown;

  for (let attempt = 0; attempt < MAX_PART_RETRIES; attempt++) {
    if (cancelledIds.has(taskId)) throw new Error("cancelled");

    try {
      // The same widening as everywhere else a typed array meets a DOM API in
      // this codebase: a `Uint8Array` is a valid request body, and the lib
      // types have not caught up with the `ArrayBufferLike` generic.
      const response = await fetch(url, {
        method: "PUT",
        body: body as unknown as BodyInit,
        ...(contentType
          ? { headers: { "content-type": contentType } }
          : {}),
      });

      if (response.ok) {
        const etag = response.headers.get("etag");

        if (etag) return etag;

        // The storage service accepted the bytes but the browser cannot see
        // the ETag, which means the bucket's CORS rule does not expose it.
        // Multipart completion is impossible without it, so fail loudly here
        // rather than sending an empty string and getting an opaque validation
        // error from the API two steps later.
        if (!isSinglePart) {
          throw new Error(
            'Storage did not expose the ETag header. Add "ETag" to the bucket\'s ' +
              "CORS ExposeHeaders rule.",
          );
        }

        // A single PUT has no parts to reconcile, so the API ignores this
        // value entirely — there is nothing to fail on.
        return "single";
      }

      if (response.status >= 400 && response.status < 500) {
        throw new Error(`Upload rejected (${response.status}).`);
      }
      lastError = new Error(`Storage error (${response.status}).`);
    } catch (error) {
      if (error instanceof Error && error.message.startsWith("Upload rejected"))
        throw error;
      lastError = error;
    }

    await new Promise((resolve) => setTimeout(resolve, 2 ** attempt * 400));
  }

  throw lastError instanceof Error
    ? lastError
    : new Error("Upload failed after several attempts.");
}
